/**
 * The everyday sounds of a cave (05 §2): digging, collecting, landing, pushing, the magic
 * wall's conversion. Stepping into space is silent, as it was in 1984. Pure.
 */

import { midiToHz } from '../notes';
import { intBelow, vary } from '../rand';
import { adsr } from '../voice';
import { hit, type Sfx, tone } from './kit';

/** A very short, soft, low-passed crunch of noise. */
export const dig: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 5200, 0.2), 0.05, 0.2),
    to: 2400,
    glide: 0.05,
    filter: { type: 'lowpass', freq: vary(r, 2600, 0.15), q: 0 },
  },
];

/** A bright triangle ping somewhere in the octave above C6, with a pulse glint on the attack. */
export const collect: Sfx = (r) => {
  const f = midiToHz(84 + intBelow(r, 12));
  return [
    hit('triangle', f, 0.28, 0.36),
    { ...hit('pulse', f * 2, 0.06, 0.08), width: 0.25 },
  ];
};

/** A thump: noise whose pitch and filter fall fast, over a triangle body dropping to the floor. */
export const landBoulder: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 1400, 0.15), 0.16, 0.55),
    to: 180,
    glide: 0.14,
    filter: { type: 'lowpass', freq: vary(r, 1800, 0.1), to: 250, sweep: 0.15, q: 3 },
  },
  { ...hit('triangle', vary(r, 120, 0.1), 0.12, 0.4), to: 45, glide: 0.1 },
];

/**
 * A glassy chime at a random high pitch (E6-E7), as the original's diamonds tinkled: a triangle
 * with a faint inharmonic partial (x2.76) that dies first, which is what makes it glass.
 */
export const landDiamond: Sfx = (r) => {
  const f = midiToHz(88 + intBelow(r, 13));
  return [
    hit('triangle', f, 0.4, 0.26),
    hit('triangle', f * 2.76, 0.12, 0.07),
  ];
};

/** A low scrape: slow, gritty band-passed noise with a thin buzzing pulse beneath. */
export const push: Sfx = (r) => [
  {
    ...tone('noise', vary(r, 420, 0.2), 0.09, adsr(0.01, 0.06, 0.5, 0.05), 0.3),
    filter: { type: 'bandpass', freq: vary(r, 500, 0.15), q: 2.5 },
  },
  {
    ...tone('pulse', vary(r, 55, 0.08), 0.08, adsr(0.005, 0.05, 0.4, 0.04), 0.1),
    width: 0.12,
    filter: { type: 'lowpass', freq: 420, q: 2 },
  },
];

/** A quick shimmer: a major arpeggio cycled at twice the frame rate, high up. */
export const magicConvert: Sfx = (r) => {
  const base = 84 + intBelow(r, 6);
  const steps = [0, 4, 7, 12, 16].map((i) => midiToHz(base + i));
  return [
    {
      ...tone('triangle', steps[0] ?? 1000, 0.1, adsr(0.001, 0.12, 0.3, 0.08), 0.16),
      steps,
      stepTime: 0.022,
    },
  ];
};
